/**
 * API Configuration for Shibumi Add-in
 * Centralized configuration for API endpoints and settings
 */

/* global window */


// API Configuration
export const API_CONFIG = {
  // Base URL for the backend API.
  //
  // Always our own origin: every way this app is served puts the page and the
  // API on one host. Prod and dev are nginx serving the built dist and proxying
  // /api/v1 to the backend beside it; locally the webpack dev-server proxies it.
  // So the page it was loaded from is, by construction, the backend that owns
  // that page's data.
  //
  // This used to be `process.env.API_BASE_URL || (isLocalhost ? origin : prod)`.
  // Both halves were a hazard:
  //
  //   - The fallback handed every non-localhost host the hardcoded prod URL.
  //     Fine while prod is the only deployed host, silently wrong the moment
  //     there is a second one. CI builds with no API_BASE_URL set, so the dev
  //     bundle on the VM has app.novatimeai.com compiled into it -- served
  //     from dev.novatimeai.com it would read and write the PROD database.
  //
  //   - The override is real (dotenv-webpack with systemvars inlines it at
  //     build time; it is not dead code) and that is the problem: which
  //     backend a build talks to became a property of an untracked .env on
  //     whichever machine ran webpack. The test environment on the VM pointed
  //     itself at a backend by raw IP and port that way. Nothing in the repo
  //     records it, and nothing catches it drifting.
  //
  // Dropping the override is deliberate. A dev-server that must front a
  // different backend sets API_PROXY_TARGET (see webpack.config.js) instead --
  // same capability, but it moves the indirection to the proxy, where a wrong
  // value fails at once rather than shipping a bundle aimed elsewhere.
  baseURL: window.location.origin + "/api/v1",

  // Timeout for API requests (in milliseconds)
  timeout: 900000,

  // Default headers
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },

  // Retained for compatibility with older callers; auth headers are now
  // attached per request by the API service.
  refreshApiKey() {
    return undefined;
  },

  // Retry configuration
  retry: {
    attempts: 3,
    delay: 1000, // milliseconds
    backoff: 2, // exponential backoff multiplier
  },

  // Endpoints
  endpoints: {
    health: "/health",
    chat: {
      message: "/chat/message",
      messageStream: "/chat/message/stream",
      sessions: "/chat/sessions",
    },
    files: {
      upload: "/files/upload",
      uploadBase64: "/files/upload-base64",
      info: "/files/info",
      list: "/files/list",
    },
  },
};

// Feature flags
export const FEATURES = {
  streaming: true,
  sessionManagement: true,
  projectContext: true,
  errorReporting: true,
  offlineMode: false,
};

// UI Configuration
export const UI_CONFIG = {
  maxMessageLength: 10000,
  typingIndicatorDelay: 500, // milliseconds
  autoScrollDelay: 100, // milliseconds
  messageAnimationDuration: 300, // milliseconds
  statusCheckInterval: 30000, // milliseconds
};

// Error messages
export const ERROR_MESSAGES = {
  network: "Unable to connect to the server. Please check your connection.",
  server: "Server error occurred. Please try again later.",
  timeout: "Request timed out. Please try again.",
  unauthorized: "Authentication required. Please refresh the page.",
  forbidden: "Access denied. Please check your permissions.",
  notFound: "Resource not found.",
  validation: "Invalid input. Please check your message.",
  rateLimit: "You have reached the limit of 3 requests per minute. Please wait and try again.",
  unknown: "An unexpected error occurred. Please try again.",
  heavyLoad: "Heavy load on model side. Please wait a few moments and try again.",
};

export default API_CONFIG;
